<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  useFetchMedicalSchemeHealthSeekers
} from '@/hooks/use-health-insurances';
import {
  useFetchPerSchemeTariffCodesRates
} from '@/hooks/use-tariff-codes';
import {
  useSyncPracticeTypes
} from '@/hooks/use-treatment-plans';
import {
  useMedicalSchemesStore
} from '@/store/use-medical-schemes-store';
import { useRolloverStore } from '@/store/use-rollover-store';
import {
  useTariffCodeStore
} from '@/store/use-tariff-codes-store';
import { showSuccess } from '@/utils/toast';
import {
  parseHealthSeekerData,
  parseTariffCodeData,
  prepareDataForSync
} from '@/utils/utils';
import PreviewResultsDialog from '@/views/tools/previews/PreviewResultsDialog.vue';
import {
  AlertCircle, ArrowLeft, CheckCircle2,
  FileText,
  RefreshCw,
  Search,
  UserCheck,
  Users
} from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { toast } from 'vue-sonner';

const router = useRouter();

// Get store
const tariffCodesStore = useTariffCodeStore();
const medicalSchemeStore = useMedicalSchemesStore();
const rolloverStore = useRolloverStore()

const { mutate: syncPracticeTypes, isPending } = useSyncPracticeTypes()

// Destructure state with storeToRefs
const { medicalScheme } = storeToRefs(medicalSchemeStore)
// Note [Re-used Rollover Store] : Destructure state with storeToRefs
const { rolloverResults } = storeToRefs(rolloverStore)

// Props (would normally come from API)
const props = defineProps<{
  members?: any[];
  tariffRates?: any[];
}>();

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

// State
const searchQuery = ref('');
const selectedTariffCodes = ref<string[]>([]);
const selectedMembers = ref<string[]>([]);
const isSyncing = ref(false);
const isResultDialogOpen = ref(false)

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(15);

// Fetch clients data
const {
  data: fetchedMembers,
} = useFetchMedicalSchemeHealthSeekers(medicalScheme.value?.funder_id)

// Fetch tariff codes based on applicable year
const {
  data: fetchedRTariffRates,
} = useFetchPerSchemeTariffCodesRates(tariffCodesStore.getApplicableYear, medicalScheme.value?.funder_id)

// Use computed to reactively access the data
const members = computed(() => fetchedMembers.value || [])

const hasResults = computed(() => rolloverResults.value !== null)

// Use computed to reactively access the data
const tariffRates = computed(() => fetchedRTariffRates.value || [])

// Computed
const filteredMembers = computed(() => {
  if (!searchQuery.value) return members.value;
  
  const query = searchQuery.value.toLowerCase();
  return members.value.filter((item: any) =>
    item.client.first_name.toLowerCase().includes(query) ||
    item.client.last_name.toLowerCase().includes(query) ||
    item.membership_number.includes(query) ||
    item.client.email?.toLowerCase().includes(query) ||
    item.client.id_number?.includes(query)
  );
});

// Pagination
const totalPages = computed(() => {
  return Math.ceil(filteredMembers.value.length / itemsPerPage.value);
});

const startIndex = computed(() => {
  return (currentPage.value - 1) * itemsPerPage.value;
});

const endIndex = computed(() => {
  return startIndex.value + itemsPerPage.value;
});

const paginatedMembers = computed(() => {
  return filteredMembers.value.slice(startIndex.value, endIndex.value);
});

const displayPages = computed(() => {
  const pages = [];
  const maxPagesToShow = 5;
  let startPage = Math.max(1, currentPage.value - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages.value, startPage + maxPagesToShow - 1);

  if (endPage - startPage < maxPagesToShow - 1) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return pages;
});

const stats = computed(() => ({
  totalMembers: members.value.length,
  selectedMembers: selectedMembers.value.length,
  totalTariffs: tariffRates.value.length,
  selectedTariffs: selectedTariffCodes.value.length
}));

// Check if all members on CURRENT PAGE are selected
const allPageMembersSelected = computed(() => {
  if (paginatedMembers.value.length === 0) return false;
  return paginatedMembers.value.every((m: any) => 
    selectedMembers.value.includes(getMemberKey(m))
  );
});

const allTariffsSelected = computed(() => {
  return tariffRates.value.length > 0 && selectedTariffCodes.value.length === tariffRates.value.length;
});

const canSync = computed(() => {
  return selectedTariffCodes.value.length > 0 && selectedMembers.value.length > 0;
});

// Methods
const toggleTariffCode = (tariffCode: any) => {
  console.log('Selected-Tariff-Code: ', tariffCode);
  const rateId = tariffCode?.rate_id
  const parsedTariffCode = parseTariffCodeData(tariffCode)
  console.log(`Parsed-Tariff-Code:`, JSON.stringify(parsedTariffCode));
  const index = selectedTariffCodes.value.indexOf(rateId);
  if (index > -1) {
    selectedTariffCodes.value.splice(index, 1);
    // TODO: Remove from store
    tariffCodesStore.removeTariffCodeFromTariffCodesList(parsedTariffCode?.code)
  } else {
    selectedTariffCodes.value.push(rateId);
    tariffCodesStore.addTariffCodeToTariffCodeList(parsedTariffCode)
  }
  // TODO: SET STORE
  console.log(`Selected Codes Count: `, tariffCodesStore.tariffCodesCount);
};

const toggleMember = (member: any) => {
  const memberKey = getMemberKey(member);
  const parsedMember = parseHealthSeekerData(member);
  console.log(`Parsed-Member:`, JSON.stringify(parsedMember));
  const index = selectedMembers.value.indexOf(memberKey);
  if (index > -1) {
    selectedMembers.value.splice(index, 1);
    // TODO: Separate Members Store from Tariff Code Store
    tariffCodesStore.removeMemberFromMembersList(parsedMember.membership_number);
  } else {
    selectedMembers.value.push(getMemberKey(member));
    tariffCodesStore.addMemberToMembersList(parsedMember);
  }
  console.log(tariffCodesStore.membersCount);
};

// Updated to only select/deselect current page members
const toggleAllPageMembers = () => {
  if (allPageMembersSelected.value) {
    // Deselect all members on current page
    paginatedMembers.value.forEach((member: any) => {
      const memberKey = getMemberKey(member);
      const index = selectedMembers.value.indexOf(memberKey);
      if (index > -1) {
        selectedMembers.value.splice(index, 1);
        const parsedMember = parseHealthSeekerData(member);
        tariffCodesStore.removeMemberFromMembersList(parsedMember.membership_number);
      }
    });
  } else {
    // Select all members on current page
    paginatedMembers.value.forEach((member: any) => {
      const memberKey = getMemberKey(member);
      if (!selectedMembers.value.includes(memberKey)) {
        selectedMembers.value.push(memberKey);
        const parsedMember = parseHealthSeekerData(member);
        tariffCodesStore.addMemberToMembersList(parsedMember);
      }
    });
  }
};

const toggleAllTariffs = () => {
  if (allTariffsSelected.value) {
    selectedTariffCodes.value = [];
    // Store Clear TariffCodes List
    tariffCodesStore.clearTariffCodesList()
  } else {
    selectedTariffCodes.value = tariffRates.value.map(t => t.rate_id);
    const parsedTariffCodesList = tariffRates.value.map(t => parseTariffCodeData(t));
    console.log(`Parsed-Tariff-Codes: `, JSON.stringify(parsedTariffCodesList));
    tariffCodesStore.addTariffCodes(parsedTariffCodesList);
  }
  console.log(`Tariff-Codes-Count: `, tariffCodesStore.tariffCodesCount);
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const getMemberKey = (member: any) => {
  return `${member.membership_number}-${member.dependant_key}`;
};

const isMemberSelected = (member: any) => {
  return selectedMembers.value.includes(getMemberKey(member));
};

const isTariffSelected = (rateId: string) => {
  return selectedTariffCodes.value.includes(rateId);
};

const getInitials = (client: any) => {
  const first = client.first_name?.[0] || '';
  const last = client.last_name?.[0] || '';
  return `${first}${last}`.toUpperCase();
};

// Get status color
const getStatusColor = (isActive: boolean) => {
  return isActive ? 'bg-green-500' : 'bg-red-500'
}

const getPhaseName = (phase: string) => {
  const phases: Record<string, string> = {
    'SO': 'Standard Ongoing',
    'SI': 'Standard Intensive',
    'RO': 'Remote Ongoing',
    'RI': 'Remote Intensive'
  };
  return phases[phase] || phase;
};

const getPhaseClass = (phase: string) => {
  const classes: Record<string, string> = {
    'SO': 'bg-green-100 text-green-800',
    'SI': 'bg-yellow-100 text-yellow-800',
    'RO': 'bg-blue-100 text-blue-800',
    'RI': 'bg-purple-100 text-purple-800'
  };
  return classes[phase] || 'bg-gray-100 text-gray-800';
};

const handleSync = async () => {
  if (!canSync.value) {
    toast.error('Please select at least one tariff code and one member');
    return;
  }

  const syncPayload = prepareDataForSync(tariffCodesStore.membersList, tariffCodesStore.tariffCodesList);
  
  const payload = JSON.stringify(syncPayload)
  console.log(`Sync-Payload: `, JSON.stringify(syncPayload));
  isSyncing.value = true;
  
  syncPracticeTypes({ payload: payload }, {
    onSuccess: (data) => {
      showSuccess("Practice Types Syncing completed!");
      rolloverStore.setRolloverResults(data)
      isResultDialogOpen.value = true
      // Reset Selctions
      isSyncing.value = false;
      tariffCodesStore.clearAllLists();
      selectedTariffCodes.value = [];
      selectedMembers.value = [];
    },
    onError: (error: any) => {
      console.log(error.message);
      // showError(error.message || 'An error occurred during syncing');
      isSyncing.value = false;
      // Reset Selctions
      isSyncing.value = false;
      tariffCodesStore.clearAllLists();
      selectedTariffCodes.value = [];
      selectedMembers.value = [];
    }
  })
};

if (hasResults) {
  isResultDialogOpen.value = true
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount);
};
</script>

<template>
  <div class="space-y-6 p-6">
    <!-- Header -->
     <div class="flex justify-end items-center">
        <Button
          @click="router.back()"
          class="flex items-center border border-gray-300 bg-gray-200 hover:bg-sky-700 hover:text-white"
        >
          <ArrowLeft/> Back
        </Button>
      </div> 
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Sync Practice Types</h1>
        <p class="text-gray-600 mt-1">Select tariff codes and members to sync practice types</p>
      </div>
      <Button
        @click="handleSync"
        :disabled="!canSync || isSyncing"
        size="lg"
        class="gap-2 border border-sky-700"
      >
        <RefreshCw :class="['h-5 w-5', { 'animate-spin': isSyncing }]" />
        {{ isSyncing ? 'Syncing...' : 'Sync Practice Types' }}
      </Button>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <Card>
        <CardContent class="pt-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Total Members</p>
              <p class="text-3xl font-bold text-gray-900 mt-2">{{ stats.totalMembers }}</p>
            </div>
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users class="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent class="pt-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Selected Members</p>
              <p class="text-3xl font-bold text-green-600 mt-2">{{ stats.selectedMembers }}</p>
            </div>
            <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <UserCheck class="h-6 w-6 text-green-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent class="pt-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Total Tariffs</p>
              <p class="text-3xl font-bold text-gray-900 mt-2">{{ stats.totalTariffs }}</p>
            </div>
            <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileText class="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent class="pt-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Selected Tariffs</p>
              <p class="text-3xl font-bold text-orange-600 mt-2">{{ stats.selectedTariffs }}</p>
            </div>
            <div class="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <CheckCircle2 class="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    <!-- Tariff Codes Section -->
    <Card>
      <CardHeader>
        <div class="flex items-center justify-between">
          <div>
            <CardTitle>Tariff Codes</CardTitle>
            <CardDescription>Select the tariff codes to sync practice types</CardDescription>
          </div>
          <Button
            @click="toggleAllTariffs"
            variant="outline"
            size="sm"
          >
            {{ allTariffsSelected ? 'Deselect All' : 'Select All' }}
          </Button>
        </div>
      </CardHeader>
      <CardContent>

        <div class="flex flex-wrap gap-2">
          <div
            v-for="rate in tariffRates"
            :key="rate.rate_id"
            @click="toggleTariffCode(rate)"
            :class="[
              'cursor-pointer transition-all p-1 rounded-lg border',
              isTariffSelected(rate.rate_id)
                ? 'border-green-500 bg-green-100'
                : 'border-gray-100 hover:border-green-500 bg-white'
            ]"
          >
            <Badge variant="outline" class="font-mono rounded-md">
              {{ rate.tariff_code.code }}
            </Badge>
          </div>
        </div>

      </CardContent>
    </Card>

    <!-- Members Section -->
    <Card>
      <CardHeader>
        <div class="flex items-center justify-between">
          <div>
            <CardTitle>Members</CardTitle>
            <CardDescription>Select the members to apply practice types (Page {{ currentPage }} of {{ totalPages }})</CardDescription>
          </div>
          <div class="flex items-center gap-3">
            <div class="relative">
              <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                v-model="searchQuery"
                placeholder="Search members..."
                class="pl-9 w-64"
              />
            </div>
            <Button
              @click="toggleAllPageMembers"
              variant="outline"
              size="sm"
            >
              {{ allPageMembersSelected ? 'Deselect All' : 'Select All' }}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent class="p-0">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 border-b">
              <tr>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Member</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Membership</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Funder</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Option</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Date Enrolled</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="member in paginatedMembers"
                :key="getMemberKey(member)"
                @click="toggleMember(member)"
                :class="[
                  'cursor-pointer transition-colors',
                  isMemberSelected(member)
                    ? 'bg-green-400/10 hover:bg-green-400/20'
                    : 'hover:bg-gray-50'
                ]"
              >
                <td class="px-4 py-4">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-gradient-to-br from-sky-700 to-sky-900 flex items-center justify-center text-white font-semibold text-sm">
                      {{ getInitials(member.client) }}
                    </div>
                    <div>
                      <div class="font-medium text-gray-900">
                        {{ member.client.first_name }} {{ member.client.last_name }}
                      </div>
                      <div class="text-sm text-gray-600">
                        ID: {{ member.client.id_number || member.client.passport_number || '-' }}
                      </div>
                    </div>
                  </div>
                </td>
      
                <td class="px-6 py-4">
                  <div>
                    <div class="font-mono text-sm font-medium text-gray-900">{{ member.membership_number }}</div>
                    <div class="text-sm text-gray-600">Dep: {{ member.dependant_key }}</div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div>
                    <div class="font-medium text-gray-900">{{ member.funder.funder.name }}</div>
                    <Badge variant="outline" class="font-mono text-xs mt-1 py-0 border rounded-full border-yellow-700">
                      {{ member.funder.funder.scheme_code }}
                    </Badge>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div>
                    <div class="font-medium text-gray-900">{{ member.funder.code }}</div>
                    <div class="text-sm text-gray-600">{{ member.funder.name }}</div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900">{{ member.date_enrolled }}</div>
                  <div v-if="member.date_joined" class="text-sm text-gray-600">Joined: {{ member.date_joined }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="flex items-center gap-2">
                    <div :class="['w-2 h-2 rounded-full', getStatusColor(member.is_active)]"></div>
                    <span class="text-sm font-medium">{{ member.is_active ? 'Active' : 'Inactive' }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Empty State -->
          <div v-if="paginatedMembers.length === 0" class="text-center py-12">
            <AlertCircle class="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <p class="text-gray-600 text-lg font-medium">No members found</p>
            <p class="text-gray-500 text-sm mt-1">Try adjusting your search</p>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="filteredMembers.length > 0" class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div class="text-sm text-gray-700">
            Showing <span class="font-medium">{{ startIndex + 1 }}</span> to
            <span class="font-medium">{{ Math.min(endIndex, filteredMembers.length) }}</span> of
            <span class="font-medium">{{ filteredMembers.length }}</span> results
            <span class="text-green-600 font-medium ml-2">({{ stats.selectedMembers }} selected)</span>
          </div>
          <div class="flex items-center gap-2">
            <Button
              @click="previousPage"
              :disabled="currentPage === 1"
              variant="outline"
              size="sm"
            >
              Previous
            </Button>
            <Button
              v-for="page in displayPages"
              :key="page"
              @click="currentPage = page"
              :variant="currentPage === page ? 'default' : 'outline'"
              size="sm"
              class="min-w-[40px]"
            >
              {{ page }}
            </Button>
            <Button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              variant="outline"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    <!-- Action Summary -->
    <Card v-if="canSync" class="border-green-400 bg-green-100">
      <CardContent class="pt-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <CheckCircle2 class="h-8 w-8 text-green-600" />
            <div>
              <p class="font-semibold text-green-900">Ready to Sync</p>
              <p class="text-sm text-green-700">
                {{ stats.selectedTariffs }} tariff code(s) will be synced with {{ stats.selectedMembers }} member(s)
              </p>
            </div>
          </div>
          <Button
            @click="handleSync"
            :disabled="isSyncing"
            size="lg"
            class="gap-2 border border-green-600"
          >
            <RefreshCw :class="['h-5 w-5', { 'animate-spin': isSyncing }]" />
            {{ isSyncing ? 'Syncing...' : 'Sync Now' }}
          </Button>
        </div>
      </CardContent>
    </Card>
    
    <div>
      <!-- Preview Results Dialog -->
    <PreviewResultsDialog
      v-if="hasResults && isResultDialogOpen && rolloverResults"
      v-model:open="isResultDialogOpen"
      :response="[rolloverResults]"
    />
    </div>
  </div>
</template>

<style scoped>
</style>